/**
 * MSI Fee Detection Service
 *
 * When a user takes an MSI (Meses Sin Intereses) offer, banks create 3 transactions:
 * - "ABONO FINANC. COMPRAS 00MSI" - Credit for original amount (positive, income)
 * - "ALTA PARA MESES S/INTERESES" - Debit for same amount (negative, expense)
 * - "CONTRATACION BENEFICIOS 6MSI" - Fee (~3-6%) (negative, expense)
 *
 * The credit and debit cancel out. Only the fee is actual cost.
 * This service detects these transaction groups and marks the principal
 * transactions as excludedFromBudget.
 */

export interface MSITransactionCandidate {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'expense' | 'income' | 'transfer';
  cardIdentifier: string;
  excludedFromBudget?: boolean;
}

const MSI_FEE_MIN_PERCENT = 2;
const MSI_FEE_MAX_PERCENT = 10;

export interface MSIFeeGroup {
  creditTransactionId: string;
  debitTransactionId: string;
  feeTransactionId: string | null;
  principalAmount: number;
  feeAmount: number | null;
}

const MSI_CREDIT_PATTERN = /^ABONO FINANC.*MSI/i;
const MSI_DEBIT_PATTERN = /^ALTA PARA MESES/i;
const MSI_FEE_PATTERN = /^CONTRATACION BENEFICIOS.*MSI/i;

export function isMSICreditTransaction(description: string): boolean {
  return MSI_CREDIT_PATTERN.test(description);
}

export function isMSIDebitTransaction(description: string): boolean {
  return MSI_DEBIT_PATTERN.test(description);
}

export function isMSIFeeTransaction(description: string): boolean {
  return MSI_FEE_PATTERN.test(description);
}

export function isMSIPrincipalTransaction(description: string): boolean {
  return (
    isMSICreditTransaction(description) || isMSIDebitTransaction(description)
  );
}

/**
 * Detects MSI fee groups in a list of transactions.
 * An MSI group consists of:
 * - A credit transaction (ABONO FINANC...MSI) with positive amount
 * - A debit transaction (ALTA PARA MESES) with negative amount matching the credit
 * - Optionally, a fee transaction (CONTRATACION BENEFICIOS...MSI)
 */
export function detectMSIFeeGroups<T extends MSITransactionCandidate>(
  transactions: T[]
): MSIFeeGroup[] {
  const groups: MSIFeeGroup[] = [];

  // Group transactions by date and card
  const byDateAndCard = new Map<string, T[]>();
  for (const tx of transactions) {
    const key = `${tx.date}-${tx.cardIdentifier}`;
    const existing = byDateAndCard.get(key) || [];
    existing.push(tx);
    byDateAndCard.set(key, existing);
  }

  for (const sameDayTxs of byDateAndCard.values()) {
    // Find credit transactions (income type)
    const credits = sameDayTxs.filter(
      (tx) => isMSICreditTransaction(tx.description) && tx.type === 'income'
    );

    // Find debit transactions (expense type)
    const debits = sameDayTxs.filter(
      (tx) => isMSIDebitTransaction(tx.description) && tx.type === 'expense'
    );

    // Find fee transactions (expense type)
    const fees = sameDayTxs.filter(
      (tx) => isMSIFeeTransaction(tx.description) && tx.type === 'expense'
    );

    // Match credits with debits by amount (both are positive)
    for (const credit of credits) {
      const matchingDebit = debits.find(
        (debit) => debit.amount === credit.amount
      );

      if (matchingDebit) {
        // Find a fee that's roughly 2-10% of principal
        const matchingFee = fees.find((fee) => {
          const feePercent = (fee.amount / credit.amount) * 100;
          return (
            feePercent >= MSI_FEE_MIN_PERCENT &&
            feePercent <= MSI_FEE_MAX_PERCENT
          );
        });

        groups.push({
          creditTransactionId: credit.id,
          debitTransactionId: matchingDebit.id,
          feeTransactionId: matchingFee?.id || null,
          principalAmount: credit.amount,
          feeAmount: matchingFee ? matchingFee.amount : null,
        });

        // Remove matched transactions from candidates
        const debitIndex = debits.indexOf(matchingDebit);
        if (debitIndex >= 0) debits.splice(debitIndex, 1);

        if (matchingFee) {
          const feeIndex = fees.indexOf(matchingFee);
          if (feeIndex >= 0) fees.splice(feeIndex, 1);
        }
      }
    }
  }

  return groups;
}

/**
 * Returns a set of transaction IDs that should be excluded from budget
 * because they are MSI principal transactions (credit/debit pairs that cancel out).
 */
export function getMSIPrincipalTransactionIds<
  T extends MSITransactionCandidate,
>(transactions: T[]): Set<string> {
  const groups = detectMSIFeeGroups(transactions);
  const excludedIds = new Set<string>();

  for (const group of groups) {
    excludedIds.add(group.creditTransactionId);
    excludedIds.add(group.debitTransactionId);
  }

  return excludedIds;
}

/**
 * Marks MSI principal transactions as excluded from budget.
 * Returns a new array with updated excludedFromBudget flags.
 */
export function excludeMSIPrincipalTransactions<
  T extends MSITransactionCandidate,
>(transactions: T[]): T[] {
  const excludedIds = getMSIPrincipalTransactionIds(transactions);

  return transactions.map((tx) => {
    if (excludedIds.has(tx.id)) {
      return { ...tx, excludedFromBudget: true };
    }
    return tx;
  });
}
